/**
 * SEND MY LIST — the whole order in three taps and a PIN.
 *
 * Nothing here decides anything: the server owns the cut-off, the capacity,
 * who may approve and how many points land. This screen only asks, shows what
 * came back, and stays warm about it. A closed or full window is shown greyed
 * with the reason, never quietly dropped from the list.
 *
 * TWO THINGS WORTH KNOWING:
 *  · The sheet is rendered into document.body through a PORTAL. It used to be
 *    a fixed child of a transformed ancestor, and a transform makes `fixed`
 *    position against that ancestor instead of the screen — which is why it
 *    did not cover the very top strip of the page.
 *  · In a DEMO organization any code confirms an order and nothing ever locks,
 *    so the sheet says so in one friendly line and no demo screen asks anyone
 *    to invent a PIN. That is decided by the SERVER, never by this file.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Send, CalendarClock, MapPin, ShoppingBasket, X, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { SheetLayer } from '../../lib/phoneSheet';
import CelebrationBurst from '../engagement/CelebrationBurst';
import GroceryTour from './GroceryTour';
import { SEND_STEPS } from './groceryTour';

const API = (typeof window !== 'undefined' ? window.location.origin : '');

const call = (path, session, body, method) => fetch(`${API}/api/grocery${path}`, {
    method: method || (body ? 'POST' : 'GET'),
    headers: { 'Content-Type': 'application/json', 'X-Grocery-Session': session },
    body: body ? JSON.stringify(body) : undefined,
}).then(async (r) => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error('http'), { status: r.status, data });
    return data;
});

const newToken = () => `send-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
// Which mini-tour step belongs to which screen of the sheet.
const TOUR_AT = { window: 'send_window', review: 'send_confirm', pin: 'send_confirm', done: 'send_confirm' };

export default function GrocerySendList({ session, memberId, itemCount, onSent, tourState }) {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState('window');
    const [cfg, setCfg] = useState(null);
    const [windows, setWindows] = useState([]);
    const [picked, setPicked] = useState(null);
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [token, setToken] = useState(newToken);
    const [done, setDone] = useState(null);
    const [cheer, setCheer] = useState(false);
    const [lock, setLock] = useState(null);
    const [tour, setTour] = useState(false);

    const load = useCallback(() => {
        Promise.all([call('/order-settings', session), call('/delivery-windows', session)])
            .then(([c, w]) => { setCfg(c); setWindows(w.windows || []); })
            .catch(() => toast.error(t('groceryOrder.loadFailed')));
    }, [session, t]);

    useEffect(() => { if (open) load(); }, [open, load]);

    // THE MINI-TOUR: automatically the FIRST time a member taps Send my list in
    // a real organization, and never in a demo (a sales demo is not
    // interrupted). The "?" button in the sheet is the way in after that.
    useEffect(() => {
        if (!open || !tourState?.send_auto_open) return undefined;
        const timer = setTimeout(() => setTour(true), 500);
        return () => clearTimeout(timer);
    }, [open, tourState]);

    const start = () => {
        setStep('window'); setPicked(null); setPin(''); setDone(null);
        setLock(null); setToken(newToken()); setOpen(true);
    };

    const submit = async (withPin) => {
        setBusy(true);
        try {
            const r = await call('/orders', session, {
                window_id: picked.id, client_token: token,
                member_id: cfg?.member_id || memberId || null, pin: withPin || null,
                lang: (i18n.language || 'en').slice(0, 2),
            });
            setDone(r.order);
            if (r.order.status === 'placed') setCheer(true);
            onSent?.(r.order);
            setStep('done');
        } catch (e) {
            const d = e.data?.detail;
            const code = typeof d === 'object' ? d.code : d;
            if (e.status === 429 || code === 'pin_locked') {
                setLock(t('groceryOrder.pinLocked', { minutes: d?.minutes || 15 }));
                setPin('');
            } else if (code === 'pin_invalid') {
                setLock(t('groceryOrder.pinWrong', { left: d?.tries_left ?? 0 }));
                setPin('');
            } else if (String(code || '').startsWith('window_')) {
                toast.error(t('groceryOrder.windowGone'));
                setStep('window'); load();
            } else {
                toast.error(t(`groceryOrder.err.${code}`, t('groceryOrder.loadFailed')));
            }
        } finally { setBusy(false); }
    };

    const onPin = (value) => {
        const v = value.replace(/\D/g, '').slice(0, 4);
        setPin(v); setLock(null);
        if (v.length === 4) submit(v);
    };

    const windowLine = (w) => `${new Date(`${w.day}T12:00:00`).toLocaleDateString(i18n.language, {
        weekday: 'long', day: 'numeric', month: 'long' })} · ${w.start}–${w.end}`;

    if (!open) {
        return (
            <Button onClick={start} disabled={!itemCount} data-testid="grocery-send-list-btn"
                data-launcher-avoid="lift"
                className="min-h-[60px] w-full gap-2 bg-emerald-700 text-base font-bold text-white hover:bg-emerald-600">
                <Send className="h-5 w-5" />
                {t('groceryOrder.sendMyList')}
            </Button>
        );
    }

    const sheet = (
        <div className="yot-sheet z-[100] flex flex-col bg-white dark:bg-slate-950" data-grocery-sheet="true"
            data-testid="grocery-send-sheet">
            <CelebrationBurst show={cheer} message={t('groceryOrder.cheer')}
                onDone={() => setCheer(false)} testId="grocery-order-cheer" />
            <div className="flex items-center justify-between border-b px-4 py-3 dark:border-slate-800">
                <p className="text-base font-bold" data-testid="grocery-send-title">
                    {t(`groceryOrder.step.${step}`)}
                </p>
                <div className="flex items-center gap-1">
                    <button onClick={() => setTour(true)} aria-label={t('groceryTour.help')}
                        data-testid="grocery-send-tour-btn"
                        className="flex min-h-[44px] min-w-[44px] items-center justify-center text-emerald-600">
                        <HelpCircle className="h-5 w-5" />
                    </button>
                    <button onClick={() => setOpen(false)} aria-label={t('groceryOrder.close')}
                        data-testid="grocery-send-close"
                        className="flex min-h-[44px] min-w-[44px] items-center justify-center">
                        <X className="h-5 w-5" />
                    </button>
                </div>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto p-4 pb-40 yot-sheet-scroll">
                {!cfg ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-500" /> : null}

                {step === 'window' && (
                    <div className="space-y-3" data-testid="grocery-windows">
                        {windows.length ? windows.map((w) => (
                            <button key={w.id} disabled={w.state !== 'open'}
                                onClick={() => { setPicked(w); setStep('review'); }}
                                data-testid={`grocery-window-${w.id}`} data-state={w.state}
                                className={`flex w-full min-h-[64px] items-center justify-between rounded-2xl border-2 px-4 py-3 text-left ${
                                    w.state === 'open'
                                        ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10'
                                        : 'border-slate-200 bg-slate-50 opacity-70 dark:border-slate-800 dark:bg-slate-900'}`}>
                                <span>
                                    <span className="block text-base font-semibold">{windowLine(w)}</span>
                                    <span className="block text-sm text-muted-foreground" data-testid={`grocery-window-note-${w.id}`}>
                                        {w.state === 'open'
                                            ? t('groceryOrder.slotsLeft', { count: w.left })
                                            : t(`groceryOrder.why.${w.reason}`)}
                                    </span>
                                </span>
                                <CalendarClock className="h-5 w-5 shrink-0 text-emerald-600" />
                            </button>
                        )) : <p className="text-sm text-muted-foreground" data-testid="grocery-no-windows">
                            {t('groceryOrder.noWindows')}</p>}
                    </div>
                )}

                {step === 'review' && picked && (
                    <div className="space-y-3" data-testid="grocery-review">
                        <p className="text-sm text-slate-500">{t('groceryOrder.cutoffPlain', {
                            hour: cfg?.cutoff_hour_local === 18 ? '6 pm' : `${cfg?.cutoff_hour_local}:00`,
                        })}</p>
                        <div className="rounded-2xl border p-4 dark:border-slate-800">
                            <p className="flex items-center gap-2 text-base font-semibold">
                                <ShoppingBasket className="h-5 w-5 text-emerald-600" />
                                {t('groceryOrder.itemsCount', { count: itemCount })}
                            </p>
                            <p className="mt-2 flex items-center gap-2 text-sm" data-testid="grocery-review-window">
                                <CalendarClock className="h-4 w-4 text-slate-400" />{windowLine(picked)}
                            </p>
                            <p className="mt-1 flex items-start gap-2 text-sm" data-testid="grocery-review-address">
                                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                                {[cfg?.address?.street, cfg?.address?.apartment, cfg?.address?.city]
                                    .filter(Boolean).join(', ')}
                            </p>
                        </div>
                        <Button onClick={() => (cfg?.i_can_approve && cfg?.approval_required
                            ? setStep('pin') : submit(null))}
                            disabled={busy} data-testid="grocery-send-confirm"
                            className="min-h-[60px] w-full bg-emerald-600 text-base font-bold text-white hover:bg-emerald-500">
                            {busy ? <Loader2 className="h-5 w-5 animate-spin" />
                                : t(cfg?.i_can_approve ? 'groceryOrder.looksGood' : 'groceryOrder.askGrownUp')}
                        </Button>
                    </div>
                )}

                {step === 'pin' && (
                    <div className="space-y-3" data-testid="grocery-pin-step">
                        <p className="text-base">{t('groceryOrder.pinAsk', { name: cfg?.member_name || '' })}</p>
                        <input value={pin} onChange={(e) => onPin(e.target.value)} autoFocus
                            inputMode="numeric" type="password" maxLength={4}
                            data-testid="grocery-pin-input"
                            className="min-h-[60px] w-full rounded-2xl border-2 px-4 text-center text-2xl tracking-[0.5em] dark:border-slate-700 dark:bg-slate-900" />
                        {cfg?.is_demo ? (
                            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300"
                                data-testid="grocery-pin-demo-hint">{t('groceryOrder.demoAnyCode')}</p>
                        ) : null}
                        {lock ? <p className="text-sm font-semibold text-rose-600" data-testid="grocery-pin-error">{lock}</p> : null}
                        <p className="text-xs text-slate-500">{t('groceryOrder.pinWhy')}</p>
                    </div>
                )}

                {step === 'done' && done && (
                    <div className="space-y-3 text-center" data-testid="grocery-send-done">
                        <p className="text-lg font-bold">
                            {t(done.status === 'placed' ? 'groceryOrder.sentTitle' : 'groceryOrder.waitingTitle',
                                { name: done.approver_name || '' })}
                        </p>
                        <p className="text-sm text-slate-500">
                            {t(done.status === 'placed' ? 'groceryOrder.sentBody' : 'groceryOrder.waitingBody',
                                { name: done.approver_name || '' })}
                        </p>
                        {done.points_earned > 0 && (
                            <p className="text-base font-bold text-emerald-600" data-testid="grocery-order-points">
                                +{done.points_earned} {t('play.points')}
                            </p>
                        )}
                        <Button onClick={() => setOpen(false)} data-testid="grocery-send-finish"
                            className="min-h-[56px] w-full bg-emerald-600 font-bold text-white hover:bg-emerald-500">
                            {t('groceryOrder.backToList')}
                        </Button>
                    </div>
                )}
            </div>

            <GroceryTour session={session} steps={SEND_STEPS} tourKey="send" state={tourState}
                open={tour} onClose={() => setTour(false)} goTo={TOUR_AT[step]}
                onFinished={() => setTour(false)} />
        </div>
    );

    // A PORTAL, so a transformed ancestor can never shrink the overlay, and
    // the height follows the viewport the shopper can actually see.
    return <SheetLayer>{sheet}</SheetLayer>;
}
